import React, { useCallback, useId, useState } from 'react'
import { pantryColors, pantryRadii } from '@pantry/ui'
import { AddSpecFieldMenu, SPEC_LIST_FIELD_LABELS, type SpecListField } from './AddSpecFieldMenu'
import { CannabinoidTable, HideIcon, ALL_CANNABINOIDS_HIDDEN_TEXT } from './CannabinoidTable'
import { ChipInput } from './ChipInput'
import { CustomSpecificationsInput, PlusIcon, specAddButtonStyle } from './CustomSpecificationsInput'
import { MAX_CUSTOM_SPECS, MAX_EFFECTS, MAX_FLAVOURS, MAX_TERPENES } from '../../constants'
import { CANNABINOID_NAMES, emptyCustomSpec } from '../../utils/variationSpecs'
import type { CannabinoidEntry, CannabinoidName, HiddenSpecField, SpecSet } from '../../../../types/product'

// Editor for one specification set (ACD-15223): Package Volume, cannabinoid
// table, Terpenes / Effects / Flavours chip rows and typed custom
// specification cards. Used by the Specifications section (default variant,
// with hide controls and "+ Add field") and by the per-variant "Edit
// specifications" dialog (no hide controls, "+ Add specification").

// Where `renderBelow` content goes. S5 uses it for the ◆ per-variant diff lines.
export type SpecFieldSlot =
  | { kind: 'cannabinoid'; name: CannabinoidName }
  | { kind: 'volume' }
  | { kind: 'list'; field: SpecListField }
  | { kind: 'custom'; index: number }
  // After the last field, before the add control ("Only on variants:").
  | { kind: 'end' }

export interface SpecFieldsEditorProps {
  value: SpecSet
  // Partial patch with SpecSet keys (same names as the ProductDraft fields).
  onChange: (patch: Partial<SpecSet>) => void
  // Product-level hidden fields; hidden fields are not rendered but keep their values.
  hidden: readonly HiddenSpecField[]
  onHide?: (field: HiddenSpecField) => void
  onShow?: (field: HiddenSpecField) => void
  // Shows the values read-only (opacity .55, inputs disabled).
  locked?: boolean
  // Hide icons / "Hide" buttons + "+ Add field" menu. Off → a plain add button.
  showHideControls: boolean
  // Label of the add control ("Add field" / "Add specification").
  addLabel: string
  // Cells placed before Package Volume in the 3-column top grid (e.g. the
  // read-only POS Net Weight / Cannabis Equiv. fields). Never locked.
  leadingFields?: React.ReactNode
  // Set false to render the editor without the top grid / Package Volume.
  showVolume?: boolean
  renderBelow?: (slot: SpecFieldSlot) => React.ReactNode
}

const LIST_FIELDS: { field: SpecListField; placeholder: string; color: 'purple' | 'amber' | 'blue'; max: number }[] = [
  { field: 'terpenes', placeholder: 'Add terpene…', color: 'purple', max: MAX_TERPENES },
  { field: 'effects', placeholder: 'Add effect…', color: 'amber', max: MAX_EFFECTS },
  { field: 'flavours', placeholder: 'Add flavour…', color: 'blue', max: MAX_FLAVOURS },
]

const lockedStyle: React.CSSProperties = { opacity: 0.55, pointerEvents: 'none' }

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: pantryColors.gray[600],
  marginBottom: 6,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  lineHeight: 1.4,
}

const volumeInputStyle = (disabled?: boolean): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  color: pantryColors.gray[800],
  background: disabled ? pantryColors.gray[50] : pantryColors.white,
  fontFamily: 'inherit',
  outline: 'none',
})

const specRowStyle: React.CSSProperties = {
  padding: '14px 0',
  borderBottom: `1px solid ${pantryColors.gray[100]}`,
}

const specRowHeadStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }

const specRowLabelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: pantryColors.gray[600] }

const hideButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  border: 'none',
  background: 'none',
  padding: '2px 4px',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: 'inherit',
  color: pantryColors.gray[400],
  cursor: 'pointer',
}

const countPillStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  minWidth: 18,
  height: 18,
  padding: '0 6px',
  boxSizing: 'border-box',
  borderRadius: pantryRadii.pill,
  background: pantryColors.gray[100],
  color: pantryColors.gray[500],
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const SpecFieldsEditor: React.FC<SpecFieldsEditorProps> = ({
  value,
  onChange,
  hidden,
  onHide,
  onShow,
  locked = false,
  showHideControls,
  addLabel,
  leadingFields,
  showVolume = true,
  renderBelow,
}) => {
  const volumeId = useId()
  const [focusCustomIndex, setFocusCustomIndex] = useState<number | null>(null)
  const clearFocus = useCallback(() => setFocusCustomIndex(null), [])

  const visibleCannabinoids = CANNABINOID_NAMES.filter((name) => !hidden.includes(name))
  const canHide = showHideControls && Boolean(onHide) && !locked
  const lockable = locked ? lockedStyle : undefined
  const customSpecs = value.bs_custom_specifications
  const customAtLimit = customSpecs.length >= MAX_CUSTOM_SPECS

  const patchCannabinoid = (name: CannabinoidName, patch: Partial<CannabinoidEntry>) => {
    onChange({ cannabinoids: value.cannabinoids.map((c) => (c.name === name ? { ...c, ...patch } : c)) })
  }

  const addCustom = () => {
    if (customAtLimit) return
    setFocusCustomIndex(customSpecs.length)
    onChange({ bs_custom_specifications: [...customSpecs, emptyCustomSpec()] })
  }

  return (
    <div>
      {showVolume && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
          {leadingFields}
          <div style={{ minWidth: 0, ...lockable }} aria-disabled={locked || undefined}>
            <label htmlFor={volumeId} style={fieldLabelStyle}>Package Volume</label>
            <input
              id={volumeId}
              type="number"
              min={0}
              step="any"
              value={value.volume ?? ''}
              disabled={locked}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  onChange({ volume: null })
                  return
                }
                const parsed = Number(raw)
                onChange({ volume: Number.isFinite(parsed) ? parsed : null })
              }}
              placeholder="e.g. 1 unit"
              style={volumeInputStyle(locked)}
              aria-label="Package Volume"
            />
            {renderBelow?.({ kind: 'volume' })}
          </div>
        </div>
      )}

      <div style={lockable} aria-disabled={locked || undefined}>
        <CannabinoidTable
          cannabinoids={value.cannabinoids}
          onChange={patchCannabinoid}
          disabled={locked}
          visibleNames={visibleCannabinoids}
          onHide={canHide ? onHide : undefined}
          renderBelowRow={renderBelow ? (name) => renderBelow({ kind: 'cannabinoid', name }) : undefined}
          emptyText={showHideControls ? ALL_CANNABINOIDS_HIDDEN_TEXT : null}
        />

        {LIST_FIELDS.filter(({ field }) => !hidden.includes(field)).map(({ field, placeholder, color, max }) => (
          <div key={field} style={specRowStyle} data-spec-field={field}>
            <div style={specRowHeadStyle}>
              <span style={specRowLabelStyle}>{SPEC_LIST_FIELD_LABELS[field]}</span>
              {canHide && (
                <button
                  type="button"
                  onClick={() => onHide?.(field)}
                  title="Hide this field"
                  aria-label={`Hide ${SPEC_LIST_FIELD_LABELS[field]}`}
                  style={hideButtonStyle}
                >
                  <HideIcon />
                  Hide
                </button>
              )}
            </div>
            <ChipInput
              value={value[field]}
              onChange={(next) => onChange({ [field]: next })}
              placeholder={placeholder}
              chipColor={color}
              disabled={locked}
              maxItems={max}
              aria-label={SPEC_LIST_FIELD_LABELS[field]}
            />
            {renderBelow?.({ kind: 'list', field })}
          </div>
        ))}

        {customSpecs.length > 0 && (
          <div style={specRowStyle} data-spec-field="custom">
            <div style={specRowHeadStyle}>
              <span style={specRowLabelStyle}>Custom specifications</span>
              <span style={countPillStyle} aria-label={`${customSpecs.length} custom specifications`}>
                {customSpecs.length}
              </span>
            </div>
            <CustomSpecificationsInput
              value={customSpecs}
              onChange={(rows) => onChange({ bs_custom_specifications: rows })}
              disabled={locked}
              showAddButton={false}
              autoFocusIndex={focusCustomIndex}
              onAutoFocusHandled={clearFocus}
              renderBelowCard={renderBelow ? (index) => renderBelow({ kind: 'custom', index }) : undefined}
            />
          </div>
        )}

        {renderBelow?.({ kind: 'end' })}

        <div style={{ marginTop: 14 }}>
          {showHideControls ? (
            <AddSpecFieldMenu
              hidden={hidden}
              onShow={(field) => onShow?.(field)}
              onAddCustom={addCustom}
              label={addLabel}
              disabled={locked}
              customDisabled={customAtLimit}
            />
          ) : (
            <button
              type="button"
              onClick={addCustom}
              disabled={locked || customAtLimit}
              title={customAtLimit ? `Up to ${MAX_CUSTOM_SPECS} custom specifications` : undefined}
              style={specAddButtonStyle(locked || customAtLimit)}
            >
              <PlusIcon />
              {addLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
